/**
 * Admin Theme Configurations Index
 *
 * Central registry and getters for admin portal themes (full + site-specific).
 */

import type { AdminThemeConfig, AdminType, SiteType } from '@app-types/admin-theme';
import { getTenantCustomizations } from '@customize/registry';
import { fullAdminTheme } from './fullAdmin';
import { medixAdminTheme } from './siteAdmins';

const DEFAULT_SITE_THEME: SiteType = 'medix';

/**
 * Baseline admin themes plus every admin theme contributed by `customize/`.
 * Built lazily -- see the note in `theme/configs/index.ts`.
 */
let registry: Record<string, AdminThemeConfig> | null = null;

const siteAdminThemeRegistry = (): Record<string, AdminThemeConfig> => {
  if (!registry) {
    registry = getTenantCustomizations().reduce(
      (acc, tenant) => (tenant.adminTheme ? { ...acc, [tenant.code]: tenant.adminTheme } : acc),
      { medix: medixAdminTheme } as Record<string, AdminThemeConfig>
    );
  }
  return registry;
};

export function getAdminTheme(adminType: AdminType, siteType?: SiteType): AdminThemeConfig {
  if (adminType === 'full') return fullAdminTheme;
  return getSiteAdminTheme(siteType ?? DEFAULT_SITE_THEME);
}

export function getSiteAdminTheme(siteType: SiteType): AdminThemeConfig {
  return siteAdminThemeRegistry()[siteType] ?? medixAdminTheme;
}

export function hasSiteAdminTheme(siteType: SiteType): boolean {
  return siteType in siteAdminThemeRegistry();
}

export function getRegisteredAdminSiteTypes(): SiteType[] {
  return Object.keys(siteAdminThemeRegistry());
}

export { fullAdminTheme, medixAdminTheme };
